import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../hooks/useApi';
import { api } from '../services/api';
import { PageHeader } from '@/components/PageHeader';
import { PermissionRequired } from '@/components/PermissionRequired';
import { DataTable } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Badge } from '@/components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '@/components/ui/Dialog';
import { Skeleton } from '@/components/ui/Skeleton';
import type { Client } from '../types';
import { Building2, Plus, Trash2, Pencil } from 'lucide-react';

export default function ClientsPage() {
  const { t } = useTranslation();
  const { data: clients, loading, error, refetch } = useApi<Client[]>(() => api('/clients'), []);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [language, setLanguage] = useState('en');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editLanguage, setEditLanguage] = useState('en');
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState('');

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(''); setBusy(true);
    try {
      await api('/clients', { method: 'POST', body: JSON.stringify({ name, language }) });
      setName(''); setOpen(false); refetch();
    } catch (err: any) { setFormError(err.message); }
    finally { setBusy(false); }
  };

  const handleDelete = async (id: string) => {
    try { await api(`/clients/${id}`, { method: 'DELETE' }); refetch(); } catch {}
  };

  const openEdit = (client: Client) => {
    setEditId(client.id);
    setEditName(client.name);
    setEditLanguage(client.language);
    setEditOpen(true);
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editId) return;
    setEditError(''); setEditBusy(true);
    try {
      await api(`/clients/${editId}`, { method: 'PATCH', body: JSON.stringify({ name: editName, language: editLanguage }) });
      setEditOpen(false); setEditId(null); refetch();
    } catch (err: any) { setEditError(err.message); }
    finally { setEditBusy(false); }
  };

  const columns = [
    { key: 'name', header: t('name') || 'Name' },
    { key: 'language', header: t('language') || 'Language', render: (c: Client) => <span className="font-mono text-xs uppercase">{c.language}</span> },
    { key: 'status', header: t('status') || 'Status', render: (c: Client) => <Badge variant={c.active ? 'success' : 'secondary'}>{c.active ? (t('active') || 'Active') : (t('inactive') || 'Inactive')}</Badge> },
    { key: 'actions', header: '', className: 'w-24', render: (c: Client) => (
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="icon" className="h-8 w-8 text-[var(--color-muted-foreground)] hover:text-[var(--color-primary)]" data-testid="edit-client-button" onClick={() => openEdit(c)} aria-label="Edit"><Pencil className="h-4 w-4" /></Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600 hover:text-rose-700" data-testid="delete-client-button" onClick={() => handleDelete(c.id)} aria-label={t('delete') as string}><Trash2 className="h-4 w-4" /></Button>
      </div>
    )},
  ];

  const mobileRender = (c: Client) => (
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[var(--color-primary)]/10 text-[var(--color-primary)]"><Building2 className="h-4 w-4" /></div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{c.name}</div>
        <div className="text-xs text-[var(--color-muted-foreground)] uppercase">{c.language}</div>
      </div>
      <div className="flex items-center gap-2">
        <Badge variant={c.active ? 'success' : 'secondary'}>{c.active ? 'Active' : 'Inactive'}</Badge>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" onClick={() => handleDelete(c.id)} aria-label="Delete"><Trash2 className="h-4 w-4" /></Button>
      </div>
    </div>
  );

  return (
    <div className="animate-in">
      <PageHeader title={t('clients') || 'Clients'}>
        <Dialog open={open} onOpenChange={setOpen}>
          <PermissionRequired pageKey="clients" minLevel="READWRITE">
            <DialogTrigger asChild><Button data-testid="create-client-button" size="sm"><Plus className="mr-2 h-4 w-4" /> New</Button></DialogTrigger>
          </PermissionRequired>
          <DialogContent>
            <DialogHeader><DialogTitle>Create Client</DialogTitle><DialogDescription>Add a new client record.</DialogDescription></DialogHeader>
            <form id="client-form" onSubmit={handleCreate} className="space-y-4 py-2">
              {formError && <Badge variant="destructive" className="w-full justify-center">{formError}</Badge>}
              <div className="space-y-2"><Label>Name</Label><Input value={name} onChange={e=>setName(e.target.value)} required /></div>
              <div className="space-y-2">
                <Label>Language</Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="en">English</SelectItem><SelectItem value="fr">Français</SelectItem><SelectItem value="de">Deutsch</SelectItem></SelectContent>
                </Select>
              </div>
            </form>
            <DialogFooter><Button data-testid="save-client-button" type="submit" form="client-form" loading={busy} disabled={busy}>Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>
      {error && <Badge variant="destructive" className="mb-4">{error}</Badge>}
      {loading ? <div className="space-y-2"><Skeleton className="h-10 w-full" /><Skeleton className="h-12 w-full" /></div> : (
        <DataTable
          columns={columns}
          data={clients || []}
          keyExtractor={(c) => c.id}
          mobileRender={mobileRender}
          emptyState={<EmptyState icon={Building2} title={t('noData') || 'No clients'} description="Add your first client." action={<Button size="sm" onClick={()=>setOpen(true)}><Plus className="mr-2 h-4 w-4" /> New Client</Button>} />}
        />
      )}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Client</DialogTitle><DialogDescription>Update client details.</DialogDescription></DialogHeader>
          <form id="edit-client-form" onSubmit={handleEdit} className="space-y-4 py-2">
            {editError && <Badge variant="destructive" className="w-full justify-center">{editError}</Badge>}
            <div className="space-y-2"><Label>Name</Label><Input value={editName} onChange={e=>setEditName(e.target.value)} required /></div>
            <div className="space-y-2">
              <Label>Language</Label>
              <Select value={editLanguage} onValueChange={setEditLanguage}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="en">English</SelectItem><SelectItem value="fr">Français</SelectItem><SelectItem value="de">Deutsch</SelectItem></SelectContent>
              </Select>
            </div>
          </form>
          <DialogFooter><Button data-testid="save-edit-client-button" type="submit" form="edit-client-form" loading={editBusy} disabled={editBusy}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
